import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import type {RGBA} from '@Utils/color.util';
import type {DeepPartial} from '@/global';
import {CommonMethods} from '@PosterWhiteboard/common-methods';
import type {TextLineListObject, TextLineStylesObject, TextRunStylesObject, TextStyleRunObject} from '@PosterWhiteboard/items/text-item/text-style-runs.types';

/**
 * The per-selection style model of a text item: an ordered, non-overlapping list of semantic
 * style runs over the item's text (see `text-style-runs.types.ts`). Every read and write goes
 * through deep copies — the runs array is part of the jsondiffpatch-based undo snapshot, and a
 * shared reference between the live item and a snapshot silently breaks history diffing.
 */
export class TextStyleRuns extends CommonMethods {
  /**
   * Deliberately an ordinary property rather than an ES private field: duplicating a selection
   * lodash-clones the live item and everything hanging off it, and that clone rebuilds each
   * object from its prototype and copies own properties — a private field is neither, so it
   * would come back missing and every read of it would throw. Internal all the same: read
   * through {@link runs}, write through {@link replaceRuns}.
   */
  private storedRuns: TextStyleRunObject[] = [];

  public get runs(): readonly TextStyleRunObject[] {
    return this.storedRuns;
  }

  /**
   * Replaces the whole run list (updates always carry the complete list, never a partial one)
   * and re-establishes the invariants: ordered by start, no empty or all-null runs, adjacent
   * runs with deep-equal styles merged.
   */
  public replaceRuns(runs: TextStyleRunObject[]): void {
    this.storedRuns = normalizeTextStyleRuns(runs);
  }

  public clear(): void {
    this.storedRuns = [];
  }

  public hasRuns(): boolean {
    return this.storedRuns.length > 0;
  }

  /**
   * Drops or trims runs that fall outside the given text length. Used by wholesale text
   * replacements that bypass fabric's index shifting.
   */
  public clampToGraphemeCount(graphemeCount: number): void {
    this.storedRuns = normalizeTextStyleRuns(
      this.storedRuns.map((run) => ({
        ...run,
        start: Math.min(run.start, graphemeCount),
        end: Math.min(run.end, graphemeCount),
      }))
    );
  }

  public toObject(): TextStyleRunObject[] {
    return cloneDeep(this.storedRuns);
  }

  public getColors(): RGBA[] {
    const colors: RGBA[] = [];
    for (const run of this.storedRuns) {
      if (run.styles.fillColor !== null) {
        colors.push(run.styles.fillColor);
      }
      if (run.styles.fillGradient !== null) {
        colors.push(...run.styles.fillGradient.fillColor);
      }
      if (run.styles.outline?.isEnabled) {
        colors.push(run.styles.outline.color);
      }
    }
    return colors;
  }

  public getFontLicenses(): string[] {
    const licenses: string[] = [];
    for (const run of this.storedRuns) {
      if (run.styles.fontLicense !== null && !licenses.includes(run.styles.fontLicense)) {
        licenses.push(run.styles.fontLicense);
      }
    }
    return licenses;
  }
}

export const normalizeTextStyleRuns = (runs: TextStyleRunObject[]): TextStyleRunObject[] => {
  const populatedRuns = runs.filter((run) => run.start < run.end && run.start >= 0 && hasAnyRunStyleOverride(run.styles)).sort((a, b) => a.start - b.start);

  const normalized: TextStyleRunObject[] = [];
  for (const run of populatedRuns) {
    const previous = normalized[normalized.length - 1];
    if (previous?.end === run.start && isEqual(previous.styles, run.styles)) {
      previous.end = run.end;
    } else {
      normalized.push(cloneDeep(run));
    }
  }
  return normalized;
};

const hasAnyRunStyleOverride = (styles: TextRunStylesObject): boolean => {
  return Object.values(styles).some((value) => value !== null);
};

/**
 * Deep-copies an incoming per-line styles record into a complete one (updates are typed as
 * DeepPartial, but line-style entries always arrive whole).
 */
const getCompleteLineListOrNull = (list: DeepPartial<TextLineListObject> | null | undefined): TextLineListObject | null => {
  if (list?.type === undefined || list.style === undefined || list.fill === undefined) {
    return null;
  }
  return {
    type: list.type,
    style: list.style,
    fill: list.fill ? [list.fill[0] ?? 0, list.fill[1] ?? 0, list.fill[2] ?? 0, list.fill[3] ?? 1] : null,
    indent: typeof list.indent === 'number' ? list.indent : null,
  };
};

export const cloneLineStylesRecord = (lineStyles: DeepPartial<Record<string, TextLineStylesObject>>): Record<string, TextLineStylesObject> => {
  const cloned: Record<string, TextLineStylesObject> = {};
  for (const [lineIndex, entry] of Object.entries(lineStyles)) {
    if (entry !== undefined) {
      cloned[lineIndex] = {
        textAlign: entry.textAlign ?? null,
        list: getCompleteLineListOrNull(entry.list),
      };
    }
  }
  return cloned;
};
